/*@flow*/
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import VegaLiteFlex from 'comps/App/VegaLite/Flex';
import ColumnGrid from '../ColumnGrid';
import { useState, useContext, useEffect } from 'react';
import useCartoMap from 'lib/hooks/useCartoMap';
import Category from '../Category';
import { extractVectorTiles } from 'lib/getCartoTiles';
import { ChartDeckContext } from '../contexts';
import Present from 'lib/Present';
import keepNonEmptyKeys from 'lib/keepNonEmptyKeys';
import { type VectorTileUrls, type CartoResponse } from 'lib/getCartoMap';
import hash from 'lib/hash';
import * as I from 'immutable';
import { studyArea } from '../Map/studyAreaGeoJson';
import ChartContainer from '../ChartContainer';
import mkOnSelectWith from '../mkOnSelectWith';
import mkPopupEvents from '../mkPopupEvents';
import ClearButton from '../ClearButton';

let CHART_NAME;

const categoryColorDomain = [
  'Benches',
  'Big Bellys',
  'Bike Racks',
  'Bus Stop',
  'Sensor',
  'Trash Cans',
  'Tree Pits',
  'Wayfinding Maps',
];
const categoryColorRange = [
  '#D8C5A0',
  '#83AA74',
  '#76D3EB',
  '#db724f',
  '#4BC5B0',
  '#B976EB',
  '#93EB76',
  '#EB76C8',
];

const QUERY_TMPL = () => `
  SELECT cartodb_id, the_geom_webmercator, asset_type as category, asset_subtypes as subcategories
  FROM new_lab_streetscape_assets
  WHERE ST_Intersects(the_geom, ST_SetSRID(ST_GeomFromGeoJSON('${studyArea}'), 4326))
`;

const BIN_TMPL = QUERY_TMPL;

const VEGA_LITE_SPEC = {
  $schema: 'https://vega.github.io/schema/vega-lite/v3.json',
  autosize: 'fit',
  padding: { left: 10, top: 10, right: 0, bottom: 10 },
  config: {
    bar: {
      size: 5,
    },
  },

  mark: 'bar',

  selection: {
    itemSelection: {
      type: 'single',
      fields: ['category'],
    },
  },

  encoding: {
    x: {
      field: 'category',
      type: 'quantitative',
      aggregate: 'count',
    },
    y: {
      field: 'category',
      type: 'nominal',
      axis: { title: '' },
    },
    color: {
      condition: {
        selection: 'itemSelection',
        field: 'category',
        type: 'nominal',
        legend: false,
        scale: {
          domain: categoryColorDomain,
          range: categoryColorRange,
        },
      },
      value: 'gray',
    },
  },
};

const BarChart = (props: {
  onSelect: Function,
  analysisId: ?string,
  mapConfig: ?CartoResponse,
}) => {
  const { onSelect, mapConfig, analysisId } = props;

  return (
    <ChartContainer
      mapConfig={mapConfig}
      queryTransform={BIN_TMPL}
      analysisId={analysisId}
    >
      {({ data }) => {
        return (
          <VegaLiteFlex
            signals={{ onSignalItemSelection_tuple: onSelect }}
            data={{ values: data }}
            spec={VEGA_LITE_SPEC}
          />
        );
      }}
    </ChartContainer>
  );
};

const DATAVIEW_1 = 'asset-invetory-widget';
const ANALYSIS_1 = 'asset-invetory-data';
const LAYER_1 = 'asset-invetory';

const INIT_CONFIG = {
  buffersize: {
    mvt: 0,
  },
  analyses: [
    {
      id: ANALYSIS_1,
      type: 'source',
      params: {
        query: QUERY_TMPL(),
      },
    },
  ],
  layers: [
    {
      id: LAYER_1,
      type: 'cartodb',
      options: {
        source: {
          id: ANALYSIS_1,
        },
      },
    },
  ],
  dataviews: {
    [DATAVIEW_1]: {
      type: 'aggregation',
      source: {
        id: ANALYSIS_1,
      },
      options: {
        aggregation: 'count',
        column: 'category',
      },
    },
  },
};

const getMapboxTiles = ({
  mapTiles,
  cartoLayerName,
}: {
  mapTiles: VectorTileUrls,
  cartoLayerName: string,
}) => [
  {
    id: `${cartoLayerName}-source`,
    type: 'source',
    config: {
      type: 'vector',
      tiles: mapTiles,
      maxzoom: 18,
    },
  },
  {
    id: `${cartoLayerName}-layer`,
    type: 'layer',
    config: {
      type: 'circle',
      source: `${cartoLayerName}-source`,
      // Note: this needs to be the id of the cartodb layer.
      'source-layer': cartoLayerName,
      paint: {
        'circle-radius': ['step', ['zoom'], 3, 15, 4],
        'circle-color': [
          'match',
          ['get', 'category'],
          ...I.List(categoryColorDomain)
            .zip(I.fromJS(categoryColorRange))
            .reduce((s, v) => s.concat(v), []),
          'black',
        ],
      },
    },
    events: mkPopupEvents(
      v => `
      <div>
        <h4>${CHART_NAME}</h4>
        <div><b>Category</b>: ${v.category}</div>
      </div>
    `
    ),
  },
];

export default (props: { name: string }) => {
  CHART_NAME = props.name;
  const [params, setParams] = useState({});
  const [showLayers, setShowLayers] = useState(false);
  const mapConfig = useCartoMap(params, INIT_CONFIG);
  const mapTiles = mapConfig ? extractVectorTiles(mapConfig) : null;
  const { addMapTiles, removeMapTiles } = useContext(ChartDeckContext);

  useEffect(() => {
    if (mapTiles) {
      const tiles = getMapboxTiles({
        mapTiles,
        cartoLayerName: LAYER_1,
      });
      if (showLayers) {
        addMapTiles(tiles);
      } else {
        removeMapTiles(tiles);
      }
    }
  }, [mapTiles && mapTiles[0], showLayers, hash(params)]);

  useEffect(() => {
    setTimeout(() => Present.is(params.filters) && setShowLayers(true), 100);
  }, [hash(params.filters)]);

  return (
    <div
      css={css(
        `
      display: grid;
      grid-template-rows: 2rem 1fr;
      grid-column-gap: 10px;
      height: 100%;
      `
      )}
    >
      <div css={css('padding-top: 5px; padding-bottom: 5px;')}>
        <label css={css('margin: 0; cursor: pointer;')}>
          <input
            type="checkbox"
            checked={showLayers}
            onChange={e => setShowLayers(e.target.checked)}
          />{' '}
          Show map layers
        </label>
        <ClearButton
          filters={params.filters}
          onClick={() => {
            setParams({
              filters: {},
            });
          }}
        />
      </div>
      <ColumnGrid columns="1fr 300px" padBottom="10px">
        <div
          css={css(`
          height: 100%;
          width: 95%;
          `)}
        >
          <BarChart
            mapConfig={mapConfig}
            analysisId={ANALYSIS_1}
            onSelect={mkOnSelectWith(setParams, DATAVIEW_1)}
          />
        </div>
        <div>
          <Category
            title="Category"
            id={DATAVIEW_1}
            mapConfig={mapConfig}
            onFiltersChanged={({ accepted }) => {
              const filters = keepNonEmptyKeys({
                accept: accepted,
                reject: [],
              });
              if (Present.is(filters)) {
                setParams({
                  filters: {
                    dataviews: {
                      [DATAVIEW_1]: filters,
                    },
                  },
                });
              } else if (Present.is(params)) {
                setParams({
                  filters: {},
                });
              }
            }}
          />
        </div>
      </ColumnGrid>
    </div>
  );
};
